import { matchPath } from 'react-router-dom';

/**
 * First-party usage tracking: page views and UI events, sent to the API's user_activity_log
 * (`POST portal/activity`, `../uniliveapi/app/Http/Middleware/AGENTS.md`). The API takes the user
 * from the session cookie, so nothing here says who the user is.
 *
 * `ActivityTracker` (c-wrapper) turns it on for a signed-in user and records page views; anything
 * else calls `track()`. Until it is on, `track()` does nothing.
 */

export type ActivityEvent = {
    kind: 'page_view' | 'event';
    name: string;
    occurred_at: string;
    session_id?: string;
    dwell_ms?: number;
    subject_type?: string;
    subject_id?: string;
    params?: Record<string, unknown>;
    /** The page visit this belongs to: a page view's own id, or the page an action happened on. */
    page_view_id?: string;
    page?: string;
};

export type PageDescription = {
    name: string;
    subject_type?: string;
    subject_id?: string;
};

export const ACTIVITY_ENDPOINT = '/api/v1/portal/activity';
export const FLUSH_INTERVAL_MS = 10_000;
export const FLUSH_AT = 20;
// The API refuses batches over 50 (config/activity.php `max_batch`).
const MAX_BATCH = 50;
const SESSION_KEY = 'unilive.activitySession';
// Never sent, whatever page they appear on. The API drops these too; this keeps them off the wire.
const DROPPED_QUERY_KEYS = /token|password|secret|authorization|email|jwt/i;

let enabled = false;
// The page the user is on, so actions and API calls can say which visit they came from.
let currentPage: { id: string; name: string; subject_type?: string; subject_id?: string } | null =
    null;
const trackListeners = new Set<(name: string, props?: Record<string, unknown>) => void>();
let queue: ActivityEvent[] = [];
let timer: ReturnType<typeof setInterval> | undefined;
let sessionId: string | undefined;

const newId = (): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** One id per browser tab: sessionStorage survives reloads in a tab and isn't shared between tabs. */
export const activitySessionId = (): string => {
    if (sessionId) return sessionId;

    try {
        sessionId = window.sessionStorage.getItem(SESSION_KEY) ?? undefined;
        if (!sessionId) {
            sessionId = newId();
            window.sessionStorage.setItem(SESSION_KEY, sessionId);
        }
    } catch {
        sessionId = newId();
    }

    return sessionId;
};

export const setActivityEnabled = (on: boolean): void => {
    enabled = on;

    if (timer) clearInterval(timer);
    timer = undefined;

    if (on) {
        timer = setInterval(() => flushActivity(), FLUSH_INTERVAL_MS);
    } else {
        queue = [];
        currentPage = null;
    }
};

/** Sends what is queued. `unloading` uses sendBeacon, which survives the page going away. */
export const flushActivity = (unloading = false): void => {
    while (queue.length > 0) {
        const body = JSON.stringify({ events: queue.splice(0, MAX_BATCH) });

        if (unloading && typeof navigator.sendBeacon === 'function') {
            navigator.sendBeacon(ACTIVITY_ENDPOINT, new Blob([body], { type: 'application/json' }));
        } else {
            // Bookkeeping: a failed send is dropped, never retried or shown.
            fetch(ACTIVITY_ENDPOINT, {
                method: 'POST',
                credentials: 'same-origin',
                keepalive: true,
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body,
            }).catch(() => undefined);
        }
    }
};

const push = (event: Omit<ActivityEvent, 'occurred_at' | 'session_id'>): void => {
    if (!enabled) return;

    queue.push({
        page_view_id: currentPage?.id,
        page: currentPage?.name,
        ...event,
        occurred_at: new Date().toISOString(),
        session_id: activitySessionId(),
    });

    if (queue.length >= FLUSH_AT) flushActivity();
};

/** A UI action worth counting: an export, a PDF, a tab switch. Names are snake_case verbs. */
export const track = (name: string, props?: Record<string, unknown>): void => {
    if (!enabled) return;
    push({ kind: 'event', name, params: props });
    trackListeners.forEach(listener => listener(name, props));
};

/**
 * Also hear every tracked action (ClarityInitialiser sends them to Clarity as events). Returns the
 * unsubscribe. Only actions recorded while tracking is on are heard.
 */
export const onTrack = (
    listener: (name: string, props?: Record<string, unknown>) => void,
): (() => void) => {
    trackListeners.add(listener);
    return () => {
        trackListeners.delete(listener);
    };
};

/** Starts a page visit: actions and API calls from now on carry its id until the next one. */
export const startPageView = (page: PageDescription): string => {
    const id = newId();
    currentPage = { id, ...page };
    return id;
};

/**
 * Headers every API call carries while a page is open (ActivityTracker adds them through an axios
 * interceptor). The API's LogsApiActivity stores them on the request's row, which is how a query
 * is grouped under the page that made it.
 */
export const activityHeaders = (): Record<string, string> =>
    currentPage
        ? { 'X-Activity-Page-View': currentPage.id, 'X-Activity-Page': currentPage.name }
        : {};

/** Records the end of a page visit started with `startPageView`. */
export const recordPageView = (
    page: PageDescription,
    dwellMs: number,
    search: string,
    pageViewId?: string,
): void => {
    push({
        kind: 'page_view',
        ...page,
        page_view_id: pageViewId,
        page: page.name,
        dwell_ms: Math.max(0, Math.round(dwellMs)),
        params: searchParams(search),
    });
    if (pageViewId && currentPage?.id === pageViewId) currentPage = null;
};

const searchParams = (search: string): Record<string, string> | undefined => {
    const params: Record<string, string> = {};

    new URLSearchParams(search).forEach((value, key) => {
        if (!DROPPED_QUERY_KEYS.test(key)) params[key] = value;
    });

    return Object.keys(params).length > 0 ? params : undefined;
};

/**
 * The route template a path belongs to, and the record it is about.
 *
 * When several templates match (`/campaigns/new` and `/campaigns/:id`), the one with the fewest
 * parameters wins. The subject is the first parameter, typed by the segment before it
 * (`/campaigns/:id/...` is campaign :id), to line up with the API's subject_type. A path no
 * template knows keeps its shape with the numbers taken out.
 */
export const describePage = (pathname: string, templates: string[]): PageDescription => {
    let best: { template: string; params: Record<string, string | undefined> } | undefined;

    templates.forEach(template => {
        const match = matchPath<Record<string, string | undefined>>(pathname, {
            path: template,
            exact: true,
        });
        if (!match) return;
        if (!best || Object.keys(match.params).length < Object.keys(best.params).length) {
            best = { template, params: match.params };
        }
    });

    if (!best) return { name: pathname.replace(/\d+/g, ':id') };

    const segments = best.template.split('/');
    const firstParam = segments.findIndex(segment => segment.startsWith(':'));
    const subjectId = firstParam > 0 ? best.params[segments[firstParam].slice(1)] : undefined;

    if (subjectId === undefined) return { name: best.template };

    return {
        name: best.template,
        subject_type: segments[firstParam - 1].replace(/s$/, '').replace(/-/g, '_'),
        subject_id: subjectId,
    };
};

/** Visible time only: paused while the tab is hidden. */
export const createDwellTimer = (now: () => number = () => performance.now()) => {
    let total = 0;
    let since: number | undefined = now();

    return {
        pause: () => {
            if (since !== undefined) total += now() - since;
            since = undefined;
        },
        resume: () => {
            if (since === undefined) since = now();
        },
        elapsed: () => total + (since !== undefined ? now() - since : 0),
    };
};
